import InfoBlock from './InfoBlock'
import parse from 'html-react-parser'
import { Option } from '@mortgage-pos/types'
import CurrencyInput from './inputs/Currency'
import React, { ChangeEvent, FocusEvent, useState } from 'react'

export interface Props {
  name: string
  type: string
  value?: Record<string, string | number>
  error: string
  title?: string
  touched: boolean
  subTitle: string
  placeholder?: string
  options: Option[]
  infoTitle?: string
  infoContent?: string
  modifierClass?: string
  infoWarning?: boolean
  setValue?: (value: any) => void
  onChange: (event: ChangeEvent<HTMLInputElement>) => {}
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
}

const Income = ({
  name,
  title,
  subTitle,
  value,
  onChange,
  onBlur = (e) => {},
  placeholder,
  options,
  modifierClass,
  error,
  touched,
  setValue,
  ...rest
}: Props) => {
  const [checkedValues, setChecked] = useState(() =>
    buildCheckedValues(options, value)
  )
  const [incomeAmounts, setIncomeAmounts] = useState(() =>
    buildIncomeAmounts(options, value)
  )
  const errorClass = error && touched ? '--error' : ''
  const changeValue =
    (key: string, i: number) =>
    (e: ChangeEvent<HTMLInputElement>): {} => {
      setIncomeAmounts((state) => {
        setValue({
          ...state,
          [key]: e.target.value,
        })
        return {
          ...state,
          [key]: e.target.value,
        }
      })
      return {}
    }

  const total: number = Object.values(incomeAmounts).reduce(
    (acc: number, curr: string) => Number(acc) + Number(curr),
    0
  )

  return (
    <>
      <div
        className={`income ${modifierClass || ''} ${errorClass}`}
        data-testid={`income-${name}`}
      >
        {title ? <h3 className="income__title">{title}</h3> : null}
        <ul className="income__options">
          {options.map((option, index) => {
            const isChecked = checkedValues?.includes(option.value)
            const checkedClass = isChecked ? '--checked' : ''

            return (
              <li
                className={`income__single-option ${checkedClass}`}
                key={`income-${index}`}
              >
                <div
                  className="income__checkbox"
                  onClick={() => {
                    const updatedValue = addOrRemoveCheckedValue(
                      checkedValues,
                      option.value
                    )
                    setChecked(updatedValue)
                  }}
                >
                  <img
                    className="income__check-mark"
                    src="/assets/check-mark.svg"
                    alt="Check mark icon"
                  />
                </div>
                <div className="income__checkbox-label">
                  {parse(option.text)}
                </div>
                {isChecked ? (
                  <div className="income__textarea">
                    <CurrencyInput
                      aria-label={name}
                      key={option.text}
                      value={incomeAmounts[option.value]}
                      name={option.text}
                      onChange={changeValue(option.value, index)}
                      onBlur={onBlur}
                      placeholder={placeholder}
                      data-testid={name}
                    />
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
        {error && touched ? <InfoBlock type="danger">{error}</InfoBlock> : null}
      </div>
      <h6 className="income__total">Total: ${total.toFixed(2)}</h6>
    </>
  )
}

export default Income

const buildIncomeAmounts = (
  options: Option[],
  value?: Record<string, string | number>
): Record<string, string> =>
  options.reduce((acc, curr) => {
    // eslint-disable-next-line no-prototype-builtins
    if (value && value.hasOwnProperty(curr.value)) {
      acc[curr.value] = value[curr.value]
      return acc
    }
    acc[curr.value] = '0'
    return acc
  }, {})

const buildCheckedValues = (
  options: Option[],
  value?: Record<string, string | number>
): Array<string> =>
  options.reduce((acc, curr) => {
    if (value[curr.value] && Number(value[curr.value]) > 0) {
      acc.push(curr.value)
    }
    return acc
  }, [])

function addOrRemoveCheckedValue(
  value: string[],
  checkedItem: string
): string[] {
  const checkedValues = new Set(value)

  if (checkedValues.has(checkedItem)) {
    checkedValues.delete(checkedItem)
    return Array.from(checkedValues)
  }

  checkedValues.add(checkedItem)
  return Array.from(checkedValues)
}
